import React, { useEffect, useState } from "react";
import Button from "../../Common/FormElements/Button/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ValidatePacking from "../ValidatePacking";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import { getDelivery, updateDelivery } from "../../../REDUX/actions/delivery";
import Select from "../../Common/FormElements/Select/Select";
import { statusOptions } from "../useDeparture";
import { documentExtensions } from "../../Produits/NewProductTabContents/data";
import API from "../../../Api/Api";
import moment from "moment";
import { uploadAttachmentToStrapi } from "../../../Utils/uploadAttachmentToStrapi";
import { uploadFile } from "../../../Utils";
import {
  DownloadAddress,
  DownloadPackingList,
  handleDownload,
} from "../../../Utils/deliveryUtils";
import { toast } from "react-toastify";

const headers = (t) => [
  t("Product Name"),
  t("Ref"),
  t("Tank/Panel"),
  t("Serial Number"),
  t("Qty"),
  t("Validation"),
];

const DepartureReception = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { delivery } = useSelector((state) => state.deliveryReducer);
  const { user } = useSelector((state) => state.userReducer);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const rawPackings =
    delivery?.attributes?.commande?.data?.attributes?.packings?.data;

  const packings = React.useMemo(() => {
    if (!rawPackings || rawPackings.length === 0) return [];

    const grouped = rawPackings.reduce((acc, packing) => {
      const modelId = packing?.attributes?.model?.data?.id;

      if (!modelId) return acc;

      if (!acc[modelId]) {
        acc[modelId] = {
          ...packing,
          attributes: {
            ...packing.attributes,
            quantity: 1,
            ids: [],
          },
        };
      }

      acc[modelId].attributes.quantity += packing?.attributes?.quantity || 0;

      acc[modelId].attributes.ids.push(packing.id);

      return acc;
    }, {});

    return Object.values(grouped);
  }, [rawPackings]);

  const columns = [
    {
      name: "productName",
      render: (row) => {
        const model = row?.attributes?.model?.data;
        const { productDimensions, weight } =
          model?.attributes?.caracteristique?.data?.attributes || {};

        return (
          <div>
            <p className="mb-3">{model?.attributes?.name ?? ""}</p>
            {productDimensions && (
              <p className="mb-0">
                {t("Pallet dimensions")}
                <br />
                {productDimensions}
              </p>
            )}
            {weight && (
              <p className="mb-0">
                {t("Weight")} {weight} kg
              </p>
            )}
          </div>
        );
      },
    },
    {
      name: "ref",
      render: (row) => row?.attributes?.reference ?? "-",
    },
    {
      name: "Tank/Panel",
      render: (row) =>
        row?.attributes?.cuve && row?.attributes?.panel
          ? [row?.attributes?.cuve, row?.attributes?.panel].join("/")
          : "-",
    },
    {
      name: "serialNumber",
      render: (row) => row?.attributes?.serialNumber ?? "-",
    },
    {
      name: "qte",
      render: (row) => row?.attributes?.quantity ?? 0,
    },
    {
      name: "validation",
      render: (row) => (
        <ValidatePacking
          canUpdate={[0, 1, 2]?.includes(user?.attributes?.userRole)}
          deliveryId={delivery?.id}
          isValidated={row?.attributes?.isReceived}
          field={"isReceived"}
          ids={row?.attributes?.ids}
        />
      ),
    },
  ];

  const importMedia = async (e) => {
    setLoading("MEDIA");
    try {
      if (e.target.files.length) {
        const file = e.target.files[0];
        if (file instanceof File) {
          const { data: createdAttachment } = await API.post(
            "api/attachments",
            {
              data: {
                title: file.name,
                date: moment().utc(true).toDate(),
                deliveryStore: delivery?.id,
              },
            },
          );
          await uploadAttachmentToStrapi(
            file,
            "depot/magasin fichier",
            createdAttachment.data.id,
            "api::attachment.attachment",
            "file",
          );
        }
        dispatch(getDelivery(delivery?.id, t("Le fichier a été ajouté")));
        e.target.value = "";
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const importCRM = async (e) => {
    setLoading("CRM");
    try {
      const file = e.target.files[0];
      if (file instanceof File) {
        const id = await uploadFile(file);
        dispatch(
          updateDelivery(
            delivery?.id,
            {
              crm: id,
            },
            () => dispatch(getDelivery(delivery?.id, t("CRM has been added"))),
          ),
        );
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const downloadMedia = async (e) => {
    const attachments = delivery?.attributes?.deliveryAttachments?.data;
    if (attachments?.length > 0) {
      await handleDownload(
        attachments,
        `Livraison${
          delivery?.attributes?.number ? `-${delivery?.attributes?.number}` : ""
        }`,
      );
    } else {
      toast.error(t("No files have been added yet."));
    }
  };

  useEffect(() => {
    setComment(delivery?.attributes?.deliveryComment ?? "");
  }, [delivery?.attributes?.deliveryComment]);

  return (
    <div className="d-flex flex-column text-dark position-relative text-break">
      <div className="d-flex gap-4 align-items-center mb-3">
        <p className="px-4 py-2 br5 text-blue sBold f15 bg-white border-2 border-blue d-flex align-items-center h-100 mb-0 h-40">
          {t("Store Reception")}
        </p>
        <Button
          loading={loading === "LISTING"}
          onClick={async () => {
            setLoading("LISTING");
            await DownloadPackingList(
              delivery?.id,
              delivery?.attributes?.number,
              t,
            );
            setLoading(false);
          }}
        >
          <i className="bi bi-download me-2" />
          {t("Listing")}
        </Button>
      </div>
      <div className="overflow-x-auto mt-2 flex-grow-1">
        <table className="table table-transparent">
          <thead className="table-dimGrey text-nowrap">
            <tr className="border-none">
              {headers(t).map((header, index) => (
                <th
                  key={header}
                  scope="col"
                  className={`py-3 px-4 text-lightBlack sBold f14 ${
                    index > 0 ? "text-center" : ""
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packings?.map((packing) => (
              <tr key={packing.id}>
                {columns.map(
                  (column, index) =>
                    !column.hidden && (
                      <td
                        key={column.name}
                        className={`py-3 text-nowrap f14 text-lightBlack d-table-cell ${
                          index >= columns.length - 1 ? "ms-auto px-1" : "px-4"
                        } ${index > 0 ? "text-center" : ""}`}
                      >
                        {column.render(packing)}
                      </td>
                    ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {[3, 4]?.includes(user?.attributes?.userRole) ? (
        comment?.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-4">
            <p className="f16 mb-0 sBold">{t("Storekeeper comment")}</p>
            <p className="mb-0 p-2 br5 bg-white border-1 border-lightGrey">
              {comment}
            </p>
          </div>
        )
      ) : (
        <div className="d-flex flex-column gap-3 mt-3">
          <TextArea
            placeholder={t("Comment")}
            value={comment}
            callback={(e) => setComment(e.target.value)}
          />
          <div className="ms-auto">
            <Button
              onClick={() => {
                dispatch(
                  updateDelivery(
                    delivery?.id,
                    { deliveryComment: comment },
                    () =>
                      dispatch(
                        getDelivery(
                          delivery?.id,
                          t("Comment has been updated"),
                        ),
                      ),
                  ),
                );
              }}
            >
              {t("Save")}
            </Button>
          </div>
        </div>
      )}

      <div className="d-flex gap-4 align-items-end mt-3">
        <Button
          className="position-relative"
          loading={loading === "MEDIA"}
          onClick={
            [3, 4]?.includes(user?.attributes?.userRole) ? downloadMedia : null
          }
        >
          <i className="bi bi-download me-2" />
          {[3, 4]?.includes(user?.attributes?.userRole)
            ? t("Download Photo/Video")
            : t("Import Photo/Video")}
          {[0, 1, 2]?.includes(user?.attributes?.userRole) && (
            <label className="position-absolute top-0 start-0 w-100 h-100 pointer">
              <input
                type="file"
                style={{ display: "none" }}
                multiple
                accept={`image/*,video/*, ${documentExtensions.join(",")}`}
                onChange={importMedia}
              />
            </label>
          )}
        </Button>
        <Button
          className="position-relative"
          loading={loading === "CRM"}
          onClick={
            [3, 4]?.includes(user?.attributes?.userRole) ? downloadMedia : null
          }
        >
          <i className="bi bi-download me-2" />
          {[3, 4]?.includes(user?.attributes?.userRole)
            ? t("Download CRM")
            : t("Import CRM")}

          {[0, 1, 2]?.includes(user?.attributes?.userRole) && (
            <label className="position-absolute top-0 start-0 w-100 h-100 pointer">
              <input
                type="file"
                style={{ display: "none" }}
                multiple
                accept={`image/*,video/*, ${documentExtensions.join(",")}`}
                onChange={importCRM}
              />
            </label>
          )}
        </Button>
        <div className="d-flex flex-column w-100 max-w-200px">
          <p className="mb-2">{t("Reception Status")}</p>
          <Select
            name="deliveryStatus"
            disabled={
              ![1, 4]?.includes(user?.attributes?.userRole) ||
              !["DELIVERY", "DELIVERED", "INCIDENT"]?.includes(
                delivery?.attributes?.deliveryStatus,
              )
            }
            value={statusOptions(t).find(
              (option) => option.code === delivery?.attributes?.deliveryStatus,
            )}
            placeholder={
              statusOptions(t).find(
                (option) =>
                  option.code === delivery?.attributes?.deliveryStatus,
              )?.name
            }
            options={statusOptions(t)?.filter((option) =>
              ["DELIVERY", "DELIVERED", "INCIDENT"].includes(option.code),
            )}
            callback={(e) =>
              dispatch(
                updateDelivery(
                  delivery?.id,
                  { deliveryStatus: e.value.code },
                  () =>
                    dispatch(
                      getDelivery(delivery?.id, t("Status has been changed")),
                    ),
                ),
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DepartureReception;
